import { Component, ViewChild, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { OpportunityCardDetailService } from './../../service/opportunity-card-detail.service';
import {
  OpportunityDate,
  OpportunityDetail,
  OpportunityQuestion,
} from '@core/interface/opportunity-card-detail';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { forkJoin, take, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AmplitudeService } from '@core/services';
import { EmailModalComponent } from '@core/component';

@Component({
  selector: 'app-opportunity-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, EmailModalComponent],
  templateUrl: './opportunity-detail.component.html',
  styleUrl: './opportunity-detail.component.scss',
})
export class OpportunityDetailComponent implements OnInit, AfterViewInit {

  opportunityCardDetails: OpportunityQuestion[] = [];
  opportunityDates: OpportunityDate[] = [];
  lastUpdated = '';
  isGlobalEditMode = false;
  editValues: Record<string, string> = {};
  isSummaryCollapsed = false;
  isTranscriptCollapsed = false;
  isVideoPlaying = false;
  isVideoLoading = false;
  isUnansweredCollapsed = true;

  summaryMode: 'short' | 'extended' = 'short';
  summaryVariants: Record<'short' | 'extended', string> = {
    short: '',
    extended: ''
  };
  isExtendedSummaryAvailable = false;
  extendedSummaryUnavailableCopy = 'Extended summary not available yet.';
  meetingTranscripts: any[] = [];
  nextSteps: string[] = [];
  title = '';
  recordingUrl = '';
  emails = [];

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('leftCol') leftCol!: ElementRef;
  @ViewChild('rightScroll') rightScroll!: ElementRef;
  @ViewChild('emailModal') emailModal!: EmailModalComponent;

  private opportunityId = '';
  botId = '';

  constructor(
    private readonly _opportunityCardDetailService: OpportunityCardDetailService,
    private readonly _route: ActivatedRoute,
    private readonly _amplitudeService: AmplitudeService
  ) { }

  ngOnInit(): void {
    this._route.params.subscribe((params) => {
      this.opportunityId = params['opportunityId'];
      this.botId = params['botId'] ?? '';
      this.loadOpportunityDates(this.opportunityId);
    });
    this.title = localStorage.getItem('OPPORTUNITY_NAME') ?? '';
  }

  ngAfterViewInit(): void {
    this.setScrollHeight();
  }

  toggleEdit(question_id: string): void {
    this.opportunityCardDetails = this.opportunityCardDetails.map(q => ({
      ...q,
      isEditMode: q.question_id === question_id ? !q.isEditMode : false
    }));
    const target = this.opportunityCardDetails.find(q => q.question_id === question_id);
    if (target) this.editValues[question_id] = target.previous_answer_summary;
  }

  cancel(question_id: string): void {
    this.opportunityCardDetails = this.opportunityCardDetails.map(q =>
      q.question_id === question_id ? { ...q, isEditMode: false } : q
    );
  }

  updateAnswer(question_id: string): void {
    this._amplitudeService.trackEvent('opportunity_detail_edit_answers', {
      opportunity_id: this.opportunityId
    });

    this.opportunityCardDetails = this.opportunityCardDetails.map(q =>
      q.question_id === question_id
        ? { ...q, previous_answer_summary: this.editValues[question_id], isEditMode: false }
        : q
    );
  }

  toggleGlobalEdit(): void {
    this.isGlobalEditMode = !this.isGlobalEditMode;
    this.editValues = this.isGlobalEditMode
      ? Object.fromEntries(
        this.opportunityCardDetails
          .filter(q => q.answers?.[0])
          .map(q => [q.question_id, q.answers![0].answer_text])
      )
      : {};
    this.setScrollHeight();
  }

  updateSingleAnswer(questionId: string): void {
    const input = this.editValues[questionId]?.trim();
    if (!input) return;

    const question = this.opportunityCardDetails.find(q => q.question_id === questionId);
    if (question) {
      question.answers = [{ answer_text: input }];
      delete this.editValues[questionId];
      if (!this.hasUnsavedChanges()) this.isGlobalEditMode = false;
    }
  }

  saveAllAnswers(): void {
    this.opportunityCardDetails.forEach(q => {
      const value = this.editValues[q.question_id]?.trim();
      if (value) {
        q.answers = [{ answer_text: value }];
      }
    });

    const answeredData = this.opportunityCardDetails.filter(
      q => q.answers && q.answers.length > 0
    );

    const requests = answeredData.map(q =>
      this._opportunityCardDetailService.updateAnswer(
        q.question_id,
        this.botId,
        q.answers[0].answer_text
      )
    );

    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: (results) => {
          console.log('All API calls completed:', results);
        },
        error: (err) => {
          console.error('One or more API calls failed:', err);
        },
      });
    }

    this.editValues = {};
    this.isGlobalEditMode = false;
    this.setScrollHeight();
  }



  cancelEdit(): void {
    this.editValues = {};
    this.isGlobalEditMode = false;
    this.setScrollHeight();
  }

  switchVersion(event: any): void {
    const meeting_bot_id = event.target.value;
    const selected = this.opportunityDates.find(d => d.meeting_bot_id === meeting_bot_id);
    const createdAt = selected?.created_at ?? '';

    this._amplitudeService.trackEvent('opportunity_detail_view_previous_updates', {
      opportunity_id: this.opportunityId,
      date_selected: createdAt,
      position: event.target.selectedIndex.toString()
    });

    this.botId = meeting_bot_id;
    this.loadAllMeetingData(meeting_bot_id, createdAt);
  }

  toggleSummary(): void {
    this.isSummaryCollapsed = !this.isSummaryCollapsed;
    this.setScrollHeight();
  }

  toggleTranscript(): void {
    this.isTranscriptCollapsed = !this.isTranscriptCollapsed;
    this.setScrollHeight();
  }

  onVideoPlay(): void {
    this.isVideoPlaying = true;
    this.isVideoLoading = false;
  }

  onVideoPause(): void {
    this.isVideoPlaying = false;
  }

  onVideoLoadStart(): void {
    this.isVideoLoading = true;
  }

  onVideoCanPlay(): void {
    this.isVideoLoading = false;
  }

  onVideoLoadedData(): void {
    this.isVideoLoading = false;
  }

  openEmailPopup(): void {
    this.emailModal.open();
  }

  hasUnsavedChanges(): boolean {
    return Object.keys(this.editValues).length > 0;
  }

  get answeredQuestions(): OpportunityQuestion[] {
    return this.opportunityCardDetails.filter(q => 
      q.answers?.[0]?.answer_text?.trim()
    );
  }

  get unansweredQuestions(): OpportunityQuestion[] {
    return this.opportunityCardDetails.filter(q => 
      !q.answers?.[0]?.answer_text?.trim()
    );
  }

  toggleUnanswered(): void {
    this.isUnansweredCollapsed = !this.isUnansweredCollapsed;
    this.setScrollHeight();
  }

  onSummaryOptionClick(mode: 'short' | 'extended', event: MouseEvent): void {
    event.stopPropagation();
    this.setSummaryMode(mode);
  }

  onSummaryToggleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleSummaryMode();
  }

  get activeSummaryDetail(): string {
    return this.summaryVariants[this.summaryMode] ?? '';
  }

  private loadOpportunityDates(opportunityId: string): void {
    this._opportunityCardDetailService.getOpportunityDates(opportunityId).subscribe((data: OpportunityDate[]) => {
      const sorted = data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      this.opportunityDates = sorted;
      this.lastUpdated = sorted[0]?.created_at ?? '';
      const targetBotId = this.botId || sorted[0]?.meeting_bot_id || '';
      this.botId = targetBotId;
      this.loadAllMeetingData(targetBotId, sorted[0]?.created_at ?? '');
    });
  }

  private loadAllMeetingData(botId: string, createdAt: string): void {
    this.getOpportunityDetail(this.opportunityId, createdAt);
    this.loadMeetingSummary(botId);
    this.loadNextSteps(botId);
    this.loadMeetingTranscript(botId);
    this.loadMeetingRecording(botId);
  }

  private getOpportunityDetail(opportunityId: string, createdAt: string): void {
    this._opportunityCardDetailService
      .getOpportunityDetail(opportunityId, createdAt)
      .subscribe((data: OpportunityDetail) => {
        this.opportunityCardDetails = data.questions.filter(q =>
          q.question_status || !!q.answers || !!q.previous_answer_summary
        );
      });
  }

  private loadMeetingSummary(meetingBotId: string): void {
    const summary$ = this._opportunityCardDetailService.getMeetingSummary(meetingBotId).pipe(take(1));
    const extended$ = this._opportunityCardDetailService
      .getExtendedSummary(meetingBotId)
      .pipe(
        take(1),
        catchError(() => of(null))
      );

    forkJoin([summary$, extended$]).subscribe({
      next: ([summaryRes, extendedRes]) => {
        const rawSummary = summaryRes?.[0]?.summary ?? '';
        const formattedShort = this.formatSummary(rawSummary);
        const highlightedShort = formattedShort || 'No summary available yet.';

        const extendedPayload = Array.isArray(extendedRes) ? extendedRes : [];
        const rawExtended = extendedPayload?.[0]?.extended_summary ?? '';
        const hasExtendedSummary = !!rawExtended?.trim();
        const formattedExtended = hasExtendedSummary ? this.formatSummary(rawExtended) : '';
        const highlightedExtended = hasExtendedSummary && formattedExtended ? formattedExtended : '';

        this.summaryVariants = {
          short: highlightedShort,
          extended: hasExtendedSummary ? highlightedExtended : this.extendedSummaryUnavailableCopy
        };

        this.isExtendedSummaryAvailable = hasExtendedSummary;
        this.summaryMode = 'short';
        this.setScrollHeight();
      },
      error: () => {
        this.summaryVariants = {
          short: 'No summary available yet.',
          extended: this.extendedSummaryUnavailableCopy
        };
        this.isExtendedSummaryAvailable = false;
        this.summaryMode = 'short';
        this.setScrollHeight();
      }
    });
  }

  private loadNextSteps(meetingBotId: string): void {
    this._opportunityCardDetailService.getNextSteps(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        const steps = res?.[0]?.next_steps ?? '';
        this.nextSteps = steps
          .replace(/\*/g, '\n')
          .split(/[\-\n]/)
          .map((step: any) => step.trim())
          .filter((step: any) => step.length > 0);
      },
      error: () => {
        this.nextSteps = [];
      }
    });
  }

  private loadMeetingTranscript(meetingBotId: string): void {
    this._opportunityCardDetailService.getTranscript(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        this.meetingTranscripts = res;
      }
    });
  }

  private loadMeetingRecording(meetingBotId: string): void {
    this.isVideoPlaying = false;
    this.isVideoLoading = false;

    if (!meetingBotId?.trim()) {
      this.recordingUrl = '';
      return;
    }

    this._opportunityCardDetailService.getMeetingRecording(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        const url = res?.recordings?.[0]?.media_shortcuts?.video_mixed?.data?.download_url;
        this.recordingUrl = url ?? '';
      },
      error: () => {
        this.recordingUrl = '';
      }
    });
  }

  private setScrollHeight(): void {
    setTimeout(() => {
      if (this.leftCol?.nativeElement && this.rightScroll?.nativeElement) {
        this.rightScroll.nativeElement.style.maxHeight = `${this.leftCol.nativeElement.offsetHeight}px`;
      }
    }, 1000);
  }

  private formatSummary(text: string): string {
    const trimmed = text?.trim();
    if (!trimmed) return '';

    let formatted = trimmed
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*/g, '<br/>• ')
      .replace(/<strong>([^<:]+):<\/strong>\s*<br\/>/g, '<strong>$1:</strong><br/>');

    const firstBreakIndex = formatted.indexOf('<br/>');
    if (firstBreakIndex !== -1) {
      formatted =
        formatted.slice(0, firstBreakIndex) +
        formatted.slice(firstBreakIndex + '<br/>'.length);
    }

    return formatted;
  }

  private setSummaryMode(mode: 'short' | 'extended'): void {
    if (mode === 'extended' && !this.isExtendedSummaryAvailable) return;
    if (this.summaryMode === mode) return;
    this.summaryMode = mode;
    this.setScrollHeight();
  }

  private toggleSummaryMode(): void {
    const mode = this.summaryMode === 'short' ? 'extended' : 'short';
    this.setSummaryMode(mode);
  }
}